import React from 'react';
import { useTranslation } from 'react-i18next';
import './schedulePrint.css';
import {
  formatScheduleTimes,
  getStatusAbbreviation,
  getStatusColor,
} from '../../utils/scheduleDisplay';

export interface SchedulePrintDay {
  date: Date;
  dateString: string;
  status: string;
  startTime?: string;
  endTime?: string;
  praktikaStartTime?: string;
  praktikaEndTime?: string;
}

export interface ScheduleMonthPrintProps {
  days: SchedulePrintDay[];
  employeeName: string;
  department: string;
  monthTitle: string;
  plannedHours: number;
  hoursBalance: number | null;
}

const WEEKDAY_KEYS = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday',
] as const;

const LEGEND_STATUSES = [
  'working',
  'day_off',
  'mothers_day',
  'remote',
  'sick_leave',
  'rest_day',
  'state_holiday',
  'business_trip',
  'military_service',
  'praktika',
] as const;

const LEGEND_LABEL_KEYS: Record<(typeof LEGEND_STATUSES)[number], string> = {
  working: 'schedule.working',
  day_off: 'schedule.dayOff',
  mothers_day: 'schedule.mothersDay',
  remote: 'schedule.remote',
  sick_leave: 'schedule.sickLeave',
  rest_day: 'schedule.restDay',
  state_holiday: 'schedule.stateHoliday',
  business_trip: 'schedule.businessTrip',
  military_service: 'schedule.militaryService',
  praktika: 'schedule.praktika',
};

function buildCalendarWeeks(days: SchedulePrintDay[]): (SchedulePrintDay | null)[][] {
  if (days.length === 0) return [];

  const dayMap = new Map(days.map((d) => [d.dateString, d]));
  const year = days[0].date.getFullYear();
  const month = days[0].date.getMonth();
  const daysInMonth = days.length;

  const weeks: (SchedulePrintDay | null)[][] = [];
  let currentWeek: (SchedulePrintDay | null)[] = [];

  const firstDay = new Date(year, month, 1);
  const jsDay = firstDay.getDay();
  const mondayOffset = jsDay === 0 ? 6 : jsDay - 1;
  for (let i = 0; i < mondayOffset; i++) {
    currentWeek.push(null);
  }

  for (let dayNum = 1; dayNum <= daysInMonth; dayNum++) {
    const dateObj = new Date(year, month, dayNum);
    const dateString = `${year}-${String(month + 1).padStart(2, '0')}-${String(dayNum).padStart(2, '0')}`;
    currentWeek.push(dayMap.get(dateString) ?? { date: dateObj, dateString, status: 'none' });

    if (currentWeek.length === 7) {
      weeks.push(currentWeek);
      currentWeek = [];
    }
  }

  if (currentWeek.length > 0) {
    while (currentWeek.length < 7) {
      currentWeek.push(null);
    }
    weeks.push(currentWeek);
  }

  return weeks;
}

const ScheduleMonthPrint: React.FC<ScheduleMonthPrintProps> = ({
  days,
  employeeName,
  department,
  monthTitle,
  plannedHours,
  hoursBalance,
}) => {
  const { t } = useTranslation();
  const weeks = React.useMemo(() => buildCalendarWeeks(days), [days]);

  const showTimes = (status: string) =>
    status === 'working' ||
    status === 'remote' ||
    status === 'business_trip' ||
    status === 'praktika';

  return (
    <div className="schedule-month-print">
      <header className="schedule-month-print__header">
        <div className="schedule-month-print__header-main">
          <h1 className="schedule-month-print__title">
            {t('schedule.printTitle', { name: employeeName, month: monthTitle })}
          </h1>
          <p className="schedule-month-print__subtitle">
            {department}
          </p>
        </div>
        <div className="schedule-month-print__summary">
          <span>
            {t('schedule.mobilePlannedHours', {
              defaultValue: 'Planuojama: {{hours}} val.',
              hours: plannedHours.toFixed(1),
            })}
          </span>
          {hoursBalance != null && (
            <span>
              {t('schedule.mobileHoursBalance', {
                defaultValue: 'Valandų balansas: {{hours}} val.',
                hours: hoursBalance.toFixed(2),
              })}
            </span>
          )}
        </div>
      </header>

      <table className="schedule-month-print__grid">
        <thead>
          <tr>
            {WEEKDAY_KEYS.map((key) => (
              <th key={key}>{t(`schedule.dayAcronyms.${key}`)}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {weeks.map((week, weekIdx) => (
            <tr key={weekIdx}>
              {week.map((day, dayIdx) => {
                if (!day) {
                  return <td key={dayIdx} className="schedule-month-print__cell schedule-month-print__cell--empty" />;
                }

                const dayNum = day.date.getDate();
                const hasStatus = day.status && day.status !== 'none';
                const hasPraktikaSeg = Boolean(day.praktikaStartTime && day.praktikaEndTime);
                const abbr = hasStatus
                  ? hasPraktikaSeg
                    ? `${getStatusAbbreviation(day.status)}+Pr`
                    : getStatusAbbreviation(day.status)
                  : '';
                const times =
                  hasStatus && showTimes(day.status)
                    ? formatScheduleTimes(day.startTime, day.endTime)
                    : '';
                const praktikaTimes = hasPraktikaSeg
                  ? formatScheduleTimes(day.praktikaStartTime, day.praktikaEndTime)
                  : '';
                const bg = hasPraktikaSeg
                  ? `linear-gradient(180deg, ${getStatusColor(day.status)} 0%, ${getStatusColor(day.status)} 48%, ${getStatusColor('praktika')} 52%, ${getStatusColor('praktika')} 100%)`
                  : hasStatus
                    ? getStatusColor(day.status)
                    : '#ffffff';

                return (
                  <td
                    key={dayIdx}
                    className="schedule-month-print__cell"
                    style={{ background: bg, backgroundColor: hasStatus ? undefined : '#ffffff' }}
                  >
                    <div className="schedule-month-print__day-num">{dayNum}</div>
                    {abbr && <div className="schedule-month-print__abbr">{abbr}</div>}
                    {times && <div className="schedule-month-print__times">{times}</div>}
                    {praktikaTimes && (
                      <div className="schedule-month-print__times">Pr {praktikaTimes}</div>
                    )}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>

      <footer className="schedule-month-print__footer">
        <span className="schedule-month-print__legend-title">
          {t('schedule.colorLegend')}:
        </span>
        <div className="schedule-month-print__legend">
          {LEGEND_STATUSES.map((status) => (
            <span key={status} className="schedule-month-print__legend-item">
              <span
                className="schedule-month-print__legend-swatch"
                style={{ backgroundColor: getStatusColor(status) }}
              />
              <span className="schedule-month-print__legend-abbr">{getStatusAbbreviation(status)}</span>
              <span>{t(LEGEND_LABEL_KEYS[status])}</span>
            </span>
          ))}
        </div>
      </footer>
    </div>
  );
};

export default ScheduleMonthPrint;
